/**
 * verdict-wording.ts — the ONE source of words for a measurement-confidence
 * verdict.
 *
 * Extracted from `TrendDelta` (`src/components/shared/trend-delta.tsx`) so the
 * client report can state a verdict without a second, drifting copy of the
 * rules. `@react-pdf/renderer` renders to PDF primitives, not the DOM: it
 * cannot mount a component built on lucide icons and Tailwind classes, so the
 * PDF genuinely cannot call `TrendDelta`. Before this module it therefore
 * carried its own hand-written mirror of the same three states, and the two
 * had already drifted — the chip said "±8pp (measured +3)" where the PDF said
 * "within measurement noise: this setup detects changes above ±8pp".
 *
 * The split is medium, not meaning: this module owns WHAT a verdict says
 * (primary slot, parenthetical suffix, screen-reader sentence, and whether the
 * state is entitled to a direction/colour at all); each surface owns how that
 * is drawn — a coloured chip with an icon in the DOM, plain text in the PDF.
 * A new verdict state is added here once and both surfaces follow.
 *
 * Pure and dependency-light on purpose (`@/lib/format` only) so the PDF route
 * never pulls a React component module into its bundle.
 */

import { pct, num, pos, NO_DATA } from '@/lib/format'

export type TrendDeltaFormat = 'pp' | 'pct' | 'num' | 'pos' | 'count'

export type TrendDeltaVerdict =
  | {
      state: 'real'
      /** Signed delta, e.g. 6 or -5. Sign drives icon direction + color (with `invert`). */
      delta: number
      format?: TrendDeltaFormat
      /** Lower-is-better metrics (position, cost): a down move is good. */
      invert?: boolean
      /** Plausible range for the change, e.g. [2, 11] or [-9, -1]. */
      range: [number, number]
    }
  | {
      state: 'noise'
      /** Detection floor (minimum detectable effect) for this setup. Always positive. */
      mde: number
      format?: TrendDeltaFormat
      /** The raw observed move — demoted to the parenthetical, never the primary slot. */
      observed: number
    }
  | {
      state: 'insufficient_data'
      /**
       * Which statistical gate failed (spec.md §7.3 / MEASUREMENT.md §3.2(b))
       * — drives the remedy noun. `'movable_prompts'` (R0.1 · T1) means the
       * cohort has enough tracked prompts and enough scans, but too few of
       * them actually changed between windows for the test to have any
       * chance of reaching significance — `needed` is not an actionable
       * target for this gate (see `verdictWording`'s handling below).
       */
      gate: 'prompts' | 'scans' | 'movable_prompts'
      /** How many more are needed to unlock a reliable comparison. */
      needed: number
    }
  | {
      state: 'unverified'
      /**
       * Signed observed delta, e.g. 6 or -5 — real data, but no statistical test has
       * been run on it (measurement-confidence Part 2). Rendered with a neutral icon
       * and always-muted colour, never green/red.
       */
      delta: number
      format?: TrendDeltaFormat
    }

/**
 * Which way the chip's arrow points and whether the state has earned a colour.
 * `'up'`/`'down'` already account for `invert`, so a consumer never re-applies
 * it — the only lower-is-better bug that can survive is one in this file.
 */
export type VerdictDirection = 'up' | 'down' | 'none'

export interface VerdictWording {
  /** The bold slot: the number the reader takes away. */
  primary: string
  /** The muted parenthetical after it. Empty string when the state has none. */
  suffix: string
  /** Full sentence for screen readers — and, in the PDF, the caption under the value. */
  ariaLabel: string
  direction: VerdictDirection
  /**
   * `true` only for `real`. `noise`, `insufficient_data` and `unverified` are
   * legitimate answers rather than bad news, so they never render coloured —
   * see the `TrendDelta` docblock.
   */
  colored: boolean
}

/**
 * Bare magnitude with its unit, e.g. `(0.4, 'pp')` → `"0.4pp"`. No sign — in
 * both surfaces the direction is carried separately (by the chip's icon, or by
 * the word in the PDF's caption), never repeated in the number.
 */
export function formatMagnitude(abs: number, format: TrendDeltaFormat): string {
  switch (format) {
    case 'pp':
      return `${abs.toFixed(1)}pp`
    case 'pct':
      // Whole numbers, "<1%" for a real sub-1% move, "0%" only for a zero (AC-11).
      return pct(abs)
    case 'pos':
      return pos(abs)
    case 'count':
      return num(Math.round(abs))
    case 'num':
    default:
      return abs.toFixed(1)
  }
}

/**
 * Same numeric convention as `formatMagnitude`, without the unit suffix — for the
 * parenthetical verdict text, where the unit is already established by the primary
 * slot (e.g. "±8pp (measured +3)", not "±8pp (measured +3pp)").
 */
function formatBareMagnitude(abs: number, format: TrendDeltaFormat): string {
  switch (format) {
    case 'pct':
      return pct(abs).replace('%', '')
    case 'count':
      return num(Math.round(abs))
    case 'pp':
    case 'pos':
    case 'num':
    default:
      return abs.toFixed(1)
  }
}

/** Signed bare magnitude, e.g. 3 → "+3.0", -3 → "−3.0". Used for observed moves. */
function formatSignedBare(n: number, format: TrendDeltaFormat): string {
  const sign = n < 0 ? '−' : '+'
  return `${sign}${formatBareMagnitude(Math.abs(n), format)}`
}

/** Bare range bound, e.g. 2 → "2.0", -9 → "−9.0". No leading "+" on positives. */
function formatBoundBare(n: number, format: TrendDeltaFormat): string {
  const sign = n < 0 ? '−' : ''
  return `${sign}${formatBareMagnitude(Math.abs(n), format)}`
}

export function verdictWording(verdict: TrendDeltaVerdict): VerdictWording {
  if (verdict.state === 'real') {
    const format = verdict.format ?? 'pp'
    const invert = verdict.invert ?? false
    const isUp = verdict.delta >= 0
    const magnitude = formatMagnitude(Math.abs(verdict.delta), format)
    const [lo, hi] = verdict.range
    const bounds = `${formatBoundBare(lo, format)} to ${formatBoundBare(hi, format)}`
    return {
      primary: `${isUp ? '+' : '−'}${magnitude}`,
      suffix: `(${bounds})`,
      ariaLabel: `Real change: ${isUp ? 'increased' : 'decreased'} ${magnitude}, likely between ${bounds}`,
      // `invert` flips which direction is GOOD, not which way the value moved —
      // the arrow keeps following the raw sign, only the colour swaps.
      direction: isUp === !invert ? 'up' : 'down',
      colored: true,
    }
  }

  if (verdict.state === 'noise') {
    const format = verdict.format ?? 'pp'
    const floor = formatMagnitude(Math.abs(verdict.mde), format)
    const observed = formatSignedBare(verdict.observed, format)
    return {
      primary: `±${floor}`,
      suffix: `(measured ${observed})`,
      ariaLabel: `Within measurement noise: this setup detects changes above ${floor}; the observed move was ${observed}`,
      direction: 'none',
      colored: false,
    }
  }

  if (verdict.state === 'insufficient_data') {
    // `movable_prompts`: enough tracked prompts and enough scans, but too few of
    // them actually changed between windows for the test to reach significance —
    // running another scan does not reliably fix this (it depends on rates
    // ceasing to tie, not on scan count), so no "N more X" action is offered.
    if (verdict.gate === 'movable_prompts') {
      return {
        primary: NO_DATA,
        suffix: '(most answers unchanged)',
        ariaLabel:
          'Not enough change yet to compare — most tracked answers stayed the same between scans, so there is nothing to test yet',
        direction: 'none',
        colored: false,
      }
    }
    const noun = verdict.gate === 'prompts' ? 'more tracked prompts' : 'more scans'
    return {
      primary: NO_DATA,
      suffix: `(${verdict.needed} ${noun})`,
      ariaLabel: `Not enough data yet to compare — ${verdict.needed} ${noun} needed for a reliable comparison`,
      direction: 'none',
      colored: false,
    }
  }

  const format = verdict.format ?? 'pp'
  const magnitude = formatMagnitude(Math.abs(verdict.delta), format)
  const primary = verdict.delta === 0 ? magnitude : `${verdict.delta > 0 ? '+' : '−'}${magnitude}`
  return {
    primary,
    suffix: '',
    ariaLabel: `Observed change, not statistically verified: ${primary}`,
    direction: 'none',
    colored: false,
  }
}
